import data from './typography.json';

/**
 * ES module wrapper around the shared typography JSON source-of-truth.
 *
 * Mirrors `./typography.cjs` so the Tailwind preset (CJS, build-time) and any
 * TypeScript consumers (ESM, run-time) read the SAME type scale — verbatim
 * Figma values from file `YZK9r3wDhvw1pY60KlVEIj`. The scale literally cannot
 * drift between web bundling, native bundling, and the Tailwind preset.
 *
 * Tailwind class consumers should prefer the generated `text-{name}` size
 * tokens (size + line-height) paired with the cross-platform weight classes
 * (see `WEIGHT_TO_CLASSES`). This TS export exists for the few places that
 * can't accept a Tailwind class — most notably React Native `StyleSheet`
 * blocks that need a raw `fontFamily` / `fontSize` / `lineHeight` (e.g. inline
 * editor/WebView styles) — via {@link nativeTextStyle}.
 */

export type TypeStyleName = keyof typeof data.styles;
export type FontWeightName = keyof typeof data.weights;

export interface TypeStyle {
  /** Original Figma text-style name (e.g. "H2", "Caption 1 Bold"). */
  figma: string;
  weight: FontWeightName;
  /** Font size in px. */
  size: number;
  /** Line height in px. */
  lineHeight: number;
  /** Letter spacing in px (0 across the whole DS). */
  letterSpacing: number;
}

export const TYPOGRAPHY = data;
export const REM_BASE = data.remBase;
export const FONT_FAMILY = data.family;

/** Figma weight name → numeric CSS weight (web `font-weight`). */
export const FONT_WEIGHTS = data.weights as Record<FontWeightName, number>;

/** Figma weight name → native Mona Sans family loaded in _layout.tsx. */
export const NATIVE_FAMILIES = data.nativeFamilies as Record<FontWeightName, string>;

export const TYPE_STYLES = data.styles as Record<TypeStyleName, TypeStyle>;

/**
 * Figma weight name → the cross-platform Tailwind weight classes to pair with
 * a `text-{name}` size token. Web resolves `font-*` against the variable Mona
 * Sans face; native selects the per-weight `.ttf` family via `font-mona-*`.
 */
export const WEIGHT_TO_CLASSES: Record<FontWeightName, string> = {
  regular: 'font-normal native:font-mona-regular',
  medium: 'font-medium native:font-mona-medium',
  semibold: 'font-semibold native:font-mona-semibold',
  bold: 'font-bold native:font-mona-bold',
};

/**
 * React Native `StyleSheet`-ready style object for a named type style — raw
 * px values plus the per-weight native font family. Use this only where a
 * Tailwind class isn't possible (inline RN styles, WebView CSS strings).
 *
 * Note: these are base (unscaled) px. NativeWind type tokens get the
 * responsive-rem treatment automatically; a raw `nativeTextStyle` does not.
 */
export function nativeTextStyle(name: TypeStyleName): {
  fontFamily: string;
  fontSize: number;
  lineHeight: number;
  letterSpacing: number;
} {
  const s = TYPE_STYLES[name];
  return {
    fontFamily: NATIVE_FAMILIES[s.weight],
    fontSize: s.size,
    lineHeight: s.lineHeight,
    letterSpacing: s.letterSpacing,
  };
}
